/* farbkombi-canvas.js - drag-and-drop yarn mood-board canvas.

   State model: a single `placed[]` array. Each entry is { id, vid, x, y }
   where vid = variant id, x/y = relative coordinates in [0,1] of the
   canvas box (so the layout survives resize). Placement state is
   persisted to localStorage and can be encoded into a URL query param
   for share links.

   Palette data is fetched once via the Storefront API (filtered to
   products with the `bonifaktur.farb_hex` metafield) and cached in
   localStorage with a 24h TTL.

   No frameworks, no libs - Pointer Events for mouse + touch.
*/
(function () {
  'use strict';

  const root = document.querySelector('[data-fk-app]');
  if (!root) return;

  const CACHE_KEY  = 'bonifaktur:fk:palette:v4';
  const STATE_KEY  = 'bonifaktur:fk:placed:v1';
  const TTL_MS     = 24 * 60 * 60 * 1000;
  const SWATCH_PX  = 96; // matches .fk-swatch width

  // ── DOM refs ─────────────────────────────────────────────────────────────

  const canvas       = root.querySelector('[data-fk-canvas]');
  const hint         = root.querySelector('[data-fk-hint]');
  const paletteList  = root.querySelector('[data-fk-palette]');
  const paletteCount = root.querySelector('[data-fk-palette-count]');
  const paletteEmpty = root.querySelector('[data-fk-palette-empty]');
  const loadingMsg   = root.querySelector('[data-fk-loading]');
  const searchInput  = root.querySelector('[data-fk-search]');
  const countEl      = root.querySelector('[data-fk-count]');
  const cartBtn      = root.querySelector('[data-fk-cart]');
  const cartLabel    = root.querySelector('[data-fk-cart-label]');
  const clearBtn     = root.querySelector('[data-fk-clear]');
  const shareBtn     = root.querySelector('[data-fk-share]');

  let palette = [];     // array of { vid, productTitle, variantTitle, vendor, image, price, handle }
  let placed  = [];     // array of { id, type, vid?, src?, x, y, scale, w?, h? }
  let selectedId = null;

  // Map from "vendor::productTitle" → array of palette entries that share
  // the same parent product. Used by the variant-swap popover.
  const siblingsMap = new Map();

  // History stack for undo/redo. Snapshots are JSON-cloned placed[] arrays.
  let history = [];
  let historyIndex = -1;
  const HIST_MAX = 30;

  const SCALE_STEPS = [0.5, 0.65, 0.8, 1, 1.25, 1.5, 1.85, 2.25];

  // ── Helpers ──────────────────────────────────────────────────────────────

  const uid = () => Math.random().toString(36).slice(2, 9);

  const findYarn = (vid) => palette.find(y => y.vid === vid);

  // ── Anonymous usage analytics ────────────────────────────────────────────
  // Fire-and-forget beacons to the Farbkombi worker so we can see which yarns,
  // colours and combinations people build. NO PII — only a random session id
  // (localStorage) groups one visitor's events into a combination; it is not
  // tied to a person. No-ops entirely when no endpoint is configured.

  const FK_ENDPOINT = (root.dataset.fkEndpoint || '').trim();
  const FK_SID_KEY  = 'bonifaktur:fk:sid:v1';

  function fkSession() {
    try {
      let s = localStorage.getItem(FK_SID_KEY);
      if (!s || !/^[A-Za-z0-9_-]{6,40}$/.test(s)) {
        s = (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 24);
        localStorage.setItem(FK_SID_KEY, s);
      }
      return s;
    } catch (_) {
      return 'anon0000sess'; // private mode: valid id, just not persisted
    }
  }

  let trackQueue = [];
  let trackTimer = 0;

  // Completion tracking. cart/share/export are captured IMMEDIATELY (so they're
  // recorded even if the visitor keeps editing afterwards or never cleanly
  // unloads — the earlier exit-only approach lost them); the page-exit fires a
  // plain 'unload'. A per-palette de-dupe stops a later, weaker trigger (e.g. a
  // tab-switch unload) from double-counting a palette already sent as 'cart'.
  const TRIGGER_RANK = { unload: 0, export: 1, share: 2, cart: 3 };
  let lastEmitKey = '';
  let lastEmitRank = -1;

  const currentVids = () => placed.filter(p => p.type !== 'image' && p.vid).map(p => p.vid);

  // Record a granular interaction (add / remove / swap / clear / example / upload).
  function track(type, entry) {
    if (!FK_ENDPOINT) return;
    const e = { type, ts: Date.now() };
    if (entry && entry.vid) {
      e.vid = entry.vid;
      if (entry.productTitle) e.product = entry.productTitle;
      if (entry.variantTitle) e.variant = entry.variantTitle;
      if (entry.vendor)       e.vendor  = entry.vendor;
    }
    trackQueue.push(e);
    clearTimeout(trackTimer);
    trackTimer = setTimeout(() => flushTrack(), 2000);
  }

  // Emit a finished-combination snapshot, de-duped per palette: only (re)send if
  // the palette changed or this trigger is stronger than the last one we sent.
  function emitCombination(trigger, vids) {
    const key = vids.slice().sort((a, b) => a - b).join(',');
    const rank = TRIGGER_RANK[trigger] || 0;
    if (key === lastEmitKey && rank <= lastEmitRank) { flushTrack(); return; }
    lastEmitKey = key;
    lastEmitRank = rank;
    flushTrack({ trigger, vids });
  }

  // A completion action (cart/share/export) — capture it right now.
  function markCompletion(trigger) {
    if (!FK_ENDPOINT) return;
    const vids = currentVids();
    if (vids.length) emitCombination(trigger, vids);
  }

  function flushTrack(snap) {
    if (!FK_ENDPOINT) return;
    if (!trackQueue.length && !snap) return;
    const payload = { session_id: fkSession(), events: trackQueue.splice(0, 50) };
    if (snap) payload.snapshot = snap;
    clearTimeout(trackTimer);
    try {
      const body = JSON.stringify(payload);
      const url  = FK_ENDPOINT.replace(/\/+$/, '') + '/ingest';
      // IMPORTANT: send as text/plain, not application/json. A JSON content-type
      // is not CORS-safelisted, which forces a preflight — and sendBeacon cannot
      // do preflighted requests, so it silently returns false and sends nothing.
      // text/plain makes this a "simple request"; the worker parses it as JSON.
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      let queued = false;
      try { queued = !!(navigator.sendBeacon && navigator.sendBeacon(url, blob)); } catch (_) { queued = false; }
      if (!queued) {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body, keepalive: true, mode: 'cors', credentials: 'omit',
        }).catch(() => {});
      }
    } catch (_) { /* analytics must never break the canvas */ }
  }

  // On page exit, record the palette they walked away with as a plain 'unload'
  // (de-dupe skips it if this palette was already sent as cart/share/export).
  function flushOnExit() {
    const vids = currentVids();
    if (!vids.length) { flushTrack(); return; }
    emitCombination('unload', vids);
  }
  if (FK_ENDPOINT) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushOnExit();
    });
    window.addEventListener('pagehide', flushOnExit);
  }

  function updateCount() {
    const yarnEntries = placed.filter(p => p.type !== 'image');
    const yarnCount = yarnEntries.length;
    const imageCount = placed.length - yarnCount;

    let totalAmount = 0;
    let currency = 'EUR';
    yarnEntries.forEach(p => {
      const yarn = findYarn(p.vid);
      if (!yarn || !yarn.price || yarn.price.amount == null) return;
      const a = parseFloat(yarn.price.amount);
      if (isFinite(a)) {
        totalAmount += a;
        if (yarn.price.currencyCode) currency = yarn.price.currencyCode;
      }
    });

    if (countEl) {
      let text;
      if (placed.length === 0) text = bfT('fk_count_zero', '0 Farben auf dem Tisch');
      else {
        const parts = [];
        if (yarnCount) parts.push(`${yarnCount} ${yarnCount === 1 ? bfT('color_one', 'Farbe') : bfT('color_other', 'Farben')}`);
        if (imageCount) parts.push(`${imageCount} ${imageCount === 1 ? bfT('image_one', 'Bild') : bfT('image_other', 'Bilder')}`);
        text = parts.join(' · ') + ' ' + bfT('on_the_table', 'auf dem Tisch');
        if (totalAmount > 0) {
          text += ' · ' + formatPrice({ amount: totalAmount.toFixed(2), currencyCode: currency });
        }
      }
      countEl.textContent = text;
    }
    canvas.dataset.hasSwatches = placed.length > 0 ? '1' : '0';
    if (cartBtn) cartBtn.disabled = yarnCount === 0;
  }

  function buildSiblingsMap() {
    siblingsMap.clear();
    palette.forEach(y => {
      const key = (y.vendor || '') + '::' + (y.productTitle || '');
      if (!siblingsMap.has(key)) siblingsMap.set(key, []);
      siblingsMap.get(key).push(y);
    });
  }

  function siblingsFor(yarn) {
    const key = (yarn.vendor || '') + '::' + (yarn.productTitle || '');
    return siblingsMap.get(key) || [];
  }

  // ── Persistence ──────────────────────────────────────────────────────────

  function savePlaced() {
    try { localStorage.setItem(STATE_KEY, JSON.stringify(placed)); }
    catch (_) { /* private mode / quota - silently skip */ }
  }

  function loadPlaced() {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Backfill new fields on entries from earlier versions.
      return parsed.map(p => ({
        type: p.type || 'yarn',
        scale: p.scale || 1,
        ...p,
      }));
    } catch (_) { return []; }
  }

  function nextScale(current, dir) {
    const idx = SCALE_STEPS.findIndex(s => Math.abs(s - current) < 0.01);
    const i = idx === -1 ? SCALE_STEPS.indexOf(1) : idx;
    return SCALE_STEPS[Math.max(0, Math.min(SCALE_STEPS.length - 1, i + dir))];
  }

  // ── Undo / redo ──────────────────────────────────────────────────────────

  function snapshot() {
    return JSON.parse(JSON.stringify(placed));
  }

  function pushHistory() {
    // Drop any redos past the current cursor.
    history = history.slice(0, historyIndex + 1);
    history.push(snapshot());
    if (history.length > HIST_MAX) {
      history.shift();
    } else {
      historyIndex++;
    }
    updateHistoryButtons();
  }

  function applySnapshot(snap) {
    placed = JSON.parse(JSON.stringify(snap));
    selectedId = null;
    closeSwapPopover();
    renderAllPlaced();
    savePlaced();
    updateCount();
  }

  function undo() {
    if (historyIndex <= 0) return;
    historyIndex--;
    applySnapshot(history[historyIndex]);
    updateHistoryButtons();
  }

  function redo() {
    if (historyIndex >= history.length - 1) return;
    historyIndex++;
    applySnapshot(history[historyIndex]);
    updateHistoryButtons();
  }

  function updateHistoryButtons() {
    const undoBtn = root.querySelector('[data-fk-undo]');
    const redoBtn = root.querySelector('[data-fk-redo]');
    if (undoBtn) undoBtn.disabled = historyIndex <= 0;
    if (redoBtn) redoBtn.disabled = historyIndex >= history.length - 1;
  }

  // ── URL share encoding ───────────────────────────────────────────────────
  //
  // Format: each placed swatch becomes "vid|x|y" (x,y as 3-decimal floats),
  // joined by ";". Then base64url-encoded. Compact enough to fit in a URL
  // even with many swatches, no JSON brackets/quotes overhead.

  function encodePlaced(arr) {
    // Yarn entries only - uploaded images are too big to URL-encode.
    const yarns = arr.filter(p => p.type !== 'image');
    if (!yarns.length) return '';
    const compact = yarns.map(p =>
      `${p.vid}|${p.x.toFixed(3)}|${p.y.toFixed(3)}|${(p.scale || 1).toFixed(2)}`
    ).join(';');
    const b64 = btoa(unescape(encodeURIComponent(compact)));
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function decodePlaced(str) {
    if (!str) return [];
    try {
      const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '='.repeat((4 - b64.length % 4) % 4);
      const compact = decodeURIComponent(escape(atob(padded)));
      return compact.split(';').filter(Boolean).map(part => {
        const [vid, x, y, scale] = part.split('|');
        return {
          id: uid(),
          type: 'yarn',
          vid: Number(vid),
          x: parseFloat(x),
          y: parseFloat(y),
          scale: scale ? parseFloat(scale) : 1,
        };
      }).filter(p => p.vid && isFinite(p.x) && isFinite(p.y));
    } catch (_) { return []; }
  }

  // ── Palette: fetch + cache ───────────────────────────────────────────────

  // Loads the palette. Returns { ok: true, data } on success or
  // { ok: false, error: string } so init() can surface diagnostics.
  async function loadPalette() {
    // Try cache first.
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached && cached.ts && Date.now() - cached.ts < TTL_MS && Array.isArray(cached.data) && cached.data.length) {
          return { ok: true, data: cached.data };
        }
      }
    } catch (_) { /* ignore */ }

    const cfgEl = document.getElementById('fk-config');
    if (!cfgEl) {
      return { ok: false, error: 'Konfiguration fehlt - Storefront-Token im Theme-Editor setzen.' };
    }
    let cfg;
    try { cfg = JSON.parse(cfgEl.textContent); }
    catch (_) { return { ok: false, error: 'Konfiguration ist ungültig.' }; }
    if (!cfg.shop || !cfg.token) {
      return { ok: false, error: 'Storefront-Token fehlt - im Theme-Editor setzen.' };
    }

    const handle = cfg.collection || 'alle-garne';
    const endpoint = `https://${cfg.shop}/api/2024-10/graphql.json`;
    // One row per variant (colorway). 50 products × up to 100 variants per
    // request - paginate as needed. Each variant gets its own image when
    // set, falling back to the product's featuredImage.
    const query = `query CollectionYarns($handle: String!, $cursor: String) {
      collection(handle: $handle) {
        id
        handle
        products(first: 50, after: $cursor) {
          pageInfo { hasNextPage endCursor }
          nodes {
            id
            handle
            title
            vendor
            featuredImage { url(transform: { maxWidth: 200, maxHeight: 200, crop: CENTER }) }
            variants(first: 100) {
              nodes {
                id
                title
                availableForSale
                price { amount currencyCode }
                image { url(transform: { maxWidth: 200, maxHeight: 200, crop: CENTER }) }
              }
            }
          }
        }
      }
    }`;

    const all = [];
    let cursor = null, hasMore = true;
    while (hasMore) {
      let res;
      try {
        res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': cfg.token,
          },
          body: JSON.stringify({ query, variables: { handle, cursor } }),
        });
      } catch (e) {
        return { ok: false, error: bfT('fk_net_error', 'Netzwerkfehler - Storefront API nicht erreichbar.') };
      }

      if (!res.ok) {
        return { ok: false, error: `Storefront API antwortete mit ${res.status}. Token oder Scopes prüfen.` };
      }

      let json;
      try { json = await res.json(); }
      catch (_) { return { ok: false, error: 'Antwort der Storefront API war kein gültiges JSON.' }; }

      if (json.errors && json.errors.length) {
        const msg = json.errors.map(e => e.message).join(' · ');
        return { ok: false, error: `GraphQL: ${msg}` };
      }

      const collection = json && json.data && json.data.collection;
      if (!collection) {
        return { ok: false, error: `Kollektion "${handle}" nicht gefunden. Handle im Theme-Editor prüfen.` };
      }

      const page = collection.products;
      page.nodes.forEach(node => {
        const productImage = node.featuredImage && node.featuredImage.url;
        node.variants.nodes.forEach(variant => {
          // Skip out-of-stock variants - listing them would only invite
          // failed cart adds. Filtering at fetch time keeps the cache
          // lean too.
          if (!variant.availableForSale) return;
          const legacyId = legacyVariantId(variant.id);
          if (!legacyId) return;
          const variantImage = variant.image && variant.image.url;
          const image = variantImage || productImage;
          // No usable image at all - skip; the chip would be empty.
          if (!image) return;
          all.push({
            vid: legacyId,
            handle: node.handle,
            productTitle: node.title,
            variantTitle: variant.title,
            vendor: node.vendor,
            image: image,
            price: variant.price,
          });
        });
      });
      hasMore = page.pageInfo.hasNextPage;
      cursor  = page.pageInfo.endCursor;
    }

    // Best-effort cache write.
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: all }));
    } catch (_) { /* quota - fine */ }

    return { ok: true, data: all };
  }

  // Storefront API returns variant IDs as "gid://shopify/ProductVariant/12345".
  // /cart/add.js wants the legacy numeric id - extract the trailing digits.
  function legacyVariantId(gid) {
    if (typeof gid !== 'string') return null;
    const match = gid.match(/(\d+)\s*$/);
    return match ? Number(match[1]) : null;
  }

  function formatPrice(price) {
    if (!price || price.amount == null) return '';
    const amount = parseFloat(price.amount);
    if (!isFinite(amount)) return '';
    const symbol = price.currencyCode === 'EUR' ? '€' : (price.currencyCode || '');
    const num = amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${num} ${symbol}`.trim();
  }

  function fullLabel(yarn) {
    const head = [yarn.vendor, yarn.productTitle].filter(Boolean).join(' · ');
    return yarn.variantTitle && yarn.variantTitle !== 'Default Title'
      ? `${head} - ${yarn.variantTitle}`
      : head;
  }

  // ── Palette rendering ────────────────────────────────────────────────────
  //
  // Two modes:
  //   1. Browse (no search): grouped by vendor in collapsible <details>.
  //      Each group's chips are rendered lazily on first expand to keep
  //      the DOM lean - with thousands of variants, eager rendering kills
  //      scroll performance.
  //   2. Search: flat list, capped at MAX_FLAT to keep responsive.

  const MAX_FLAT = 200;

  function makeChip(y) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fk-chip';
    btn.dataset.vid = String(y.vid);
    btn.draggable = true;
    const priceStr = formatPrice(y.price);
    btn.title = priceStr ? `${fullLabel(y)} · ${priceStr}` : fullLabel(y);
    btn.setAttribute('aria-label', btn.title);

    const img = document.createElement('img');
    img.src = y.image;
    img.alt = '';
    img.loading = 'lazy';
    img.className = 'fk-chip__img';
    img.draggable = false;
    btn.appendChild(img);

    attachChipDrag(btn, y);
    return btn;
  }

  function renderPalette(filter) {
    if (!paletteList) return;
    const term = (filter || '').toLowerCase().trim();
    paletteList.innerHTML = '';
    paletteList.classList.toggle('fk-palette__list--searching', !!term);

    if (term) {
      const matches = palette.filter(y =>
        (y.productTitle || '').toLowerCase().includes(term) ||
        (y.variantTitle || '').toLowerCase().includes(term) ||
        (y.vendor || '').toLowerCase().includes(term)
      );
      const shown = matches.slice(0, MAX_FLAT);
      const grid = document.createElement('div');
      grid.className = 'fk-palette__grid';
      shown.forEach(y => grid.appendChild(makeChip(y)));
      paletteList.appendChild(grid);
      if (matches.length > MAX_FLAT) {
        const note = document.createElement('p');
        note.className = 'fk-palette__more-note t-mono';
        note.textContent = `${matches.length - MAX_FLAT} weitere - Suche verfeinern.`;
        paletteList.appendChild(note);
      }
      if (paletteCount) {
        paletteCount.textContent = `${matches.length}/${palette.length}`;
      }
      if (paletteEmpty) paletteEmpty.hidden = matches.length > 0;
      return;
    }

    // Browse mode: nested brand → product → variants.
    const byVendor = new Map();
    palette.forEach(y => {
      const v = y.vendor || '-';
      if (!byVendor.has(v)) byVendor.set(v, new Map());
      const byProduct = byVendor.get(v);
      const p = y.productTitle || '-';
      if (!byProduct.has(p)) byProduct.set(p, []);
      byProduct.get(p).push(y);
    });

    const vendors = [...byVendor.keys()].sort((a, b) =>
      a.localeCompare(b, 'de', { sensitivity: 'base' })
    );

    vendors.forEach(v => {
      const byProduct = byVendor.get(v);
      const products = [...byProduct.keys()].sort((a, b) =>
        a.localeCompare(b, 'de', { sensitivity: 'base' })
      );
      const totalVariants = products.reduce((sum, p) => sum + byProduct.get(p).length, 0);

      const vendorEl = document.createElement('details');
      vendorEl.className = 'fk-palette__group fk-palette__group--vendor';
      const vSummary = document.createElement('summary');
      vSummary.className = 'fk-palette__summary';
      const vName = document.createElement('span');
      vName.className = 'fk-palette__summary-name';
      vName.textContent = v;
      const vCount = document.createElement('span');
      vCount.className = 'fk-palette__summary-count t-mono';
      vCount.textContent = String(totalVariants);
      vSummary.append(vName, vCount);
      vendorEl.appendChild(vSummary);

      const productsContainer = document.createElement('div');
      productsContainer.className = 'fk-palette__products';
      vendorEl.appendChild(productsContainer);

      let vendorBuilt = false;
      vendorEl.addEventListener('toggle', () => {
        if (!vendorEl.open || vendorBuilt) return;
        vendorBuilt = true;

        products.forEach(p => {
          const variants = byProduct.get(p);

          // Single-variant products: render the chip directly under the
          // vendor - there's nothing to expand, the product title is the
          // variant title. Multi-variant products always get their own
          // collapsible header so the user can see what they're looking at,
          // even when the brand has only one such product (otherwise a
          // brand with one product just shows a wall of unnamed colorways).
          if (variants.length === 1) {
            const grid = document.createElement('div');
            grid.className = 'fk-palette__grid fk-palette__grid--flat';
            variants.forEach(y => grid.appendChild(makeChip(y)));
            productsContainer.appendChild(grid);
            return;
          }

          const productEl = document.createElement('details');
          productEl.className = 'fk-palette__group fk-palette__group--product';
          const pSummary = document.createElement('summary');
          pSummary.className = 'fk-palette__summary fk-palette__summary--product';
          const pName = document.createElement('span');
          pName.className = 'fk-palette__summary-name';
          pName.textContent = p;
          const pCount = document.createElement('span');
          pCount.className = 'fk-palette__summary-count t-mono';
          pCount.textContent = String(variants.length);
          pSummary.append(pName, pCount);
          productEl.appendChild(pSummary);

          const grid = document.createElement('div');
          grid.className = 'fk-palette__grid';
          productEl.appendChild(grid);

          let productBuilt = false;
          productEl.addEventListener('toggle', () => {
            if (productEl.open && !productBuilt) {
              variants.forEach(y => grid.appendChild(makeChip(y)));
              productBuilt = true;
            }
          });

          productsContainer.appendChild(productEl);
        });
      });

      paletteList.appendChild(vendorEl);
    });

    if (paletteCount) {
      paletteCount.textContent = bfT('fk_palette_count', '{count} Garne · {brands} Marken').replace('{count}', palette.length).replace('{brands}', vendors.length);
    }
    if (paletteEmpty) paletteEmpty.hidden = palette.length > 0;
  }

  // ── Drag from palette → canvas ───────────────────────────────────────────

  function attachChipDrag(chip, yarn) {
    // Native HTML5 drag for desktop precision-placement.
    chip.addEventListener('dragstart', (e) => {
      chip.classList.add('fk-chip--dragging');
      try {
        e.dataTransfer.setData('text/fk-vid', String(yarn.vid));
        e.dataTransfer.effectAllowed = 'copy';
      } catch (_) { /* old browser */ }
    });
    chip.addEventListener('dragend', () => {
      chip.classList.remove('fk-chip--dragging');
    });

    // Click / tap to add - same path on desktop and mobile. Drops near
    // the canvas center with a small jitter so multiple adds don't stack.
    // After the swatch is on the canvas the user can drag it to position.
    chip.addEventListener('click', () => {
      // If the click came right after an HTML5 drag, the drag handler
      // already created the swatch; suppress the duplicate.
      if (chip.classList.contains('fk-chip--dragging')) return;
      const jitter = () => 0.42 + Math.random() * 0.16;
      addPlaced(yarn.vid, jitter(), jitter());
    });
  }

  // ── Click empty canvas → deselect ────────────────────────────────────────

  canvas.addEventListener('pointerdown', (e) => {
    if (e.target === canvas || e.target.classList.contains('fk-canvas__hint')) {
      if (selectedId) selectSwatch(null);
    }
  });

  // ── Canvas drop target (HTML5 drag) ──────────────────────────────────────

  canvas.addEventListener('dragover', (e) => {
    if (!e.dataTransfer || !e.dataTransfer.types.includes('text/fk-vid')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    canvas.classList.add('fk-canvas--drag-over');
  });

  canvas.addEventListener('dragleave', (e) => {
    if (e.target === canvas) canvas.classList.remove('fk-canvas--drag-over');
  });

  canvas.addEventListener('drop', (e) => {
    e.preventDefault();
    canvas.classList.remove('fk-canvas--drag-over');
    const vid = Number(e.dataTransfer.getData('text/fk-vid'));
    if (!vid) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top)  / rect.height;
    addPlaced(vid, clamp01(x), clamp01(y));
  });

  function clamp01(n) { return Math.max(0, Math.min(1, n)); }

  // ── Placed swatches: render + drag-on-canvas ─────────────────────────────

  function addPlaced(vid, x, y) {
    const yarn = findYarn(vid);
    if (!yarn) return;
    const entry = { id: uid(), type: 'yarn', vid, x, y, scale: 1 };
    placed.push(entry);
    savePlaced();
    pushHistory();
    renderSwatch(entry);
    updateCount();
    track('add', yarn);
  }

  function addPlacedImage(src, x, y, w, h) {
    const entry = { id: uid(), type: 'image', src, x, y, scale: 1, w, h };
    placed.push(entry);
    savePlaced();
    pushHistory();
    renderSwatch(entry);
    updateCount();
  }

  function removePlaced(id) {
    const idx = placed.findIndex(p => p.id === id);
    if (idx === -1) return;
    const removedVid = placed[idx].vid;
    placed.splice(idx, 1);
    const el = canvas.querySelector(`[data-swatch-id="${id}"]`);
    if (el) el.remove();
    if (selectedId === id) selectedId = null;
    closeSwapPopover();
    savePlaced();
    pushHistory();
    updateCount();
    if (removedVid) track('remove', { vid: removedVid });
  }

  function rescalePlaced(id, dir) {
    const entry = placed.find(p => p.id === id);
    if (!entry) return;
    entry.scale = nextScale(entry.scale || 1, dir);
    const el = canvas.querySelector(`[data-swatch-id="${id}"]`);
    if (el) el.style.setProperty('--fk-scale', entry.scale);
    savePlaced();
    pushHistory();
  }

  function bringToFront(id) {
    const idx = placed.findIndex(p => p.id === id);
    if (idx === -1 || idx === placed.length - 1) return;
    const [entry] = placed.splice(idx, 1);
    placed.push(entry);
    const el = canvas.querySelector(`[data-swatch-id="${id}"]`);
    if (el) canvas.appendChild(el);
    savePlaced();
    pushHistory();
  }

  function sendToBack(id) {
    const idx = placed.findIndex(p => p.id === id);
    if (idx === -1 || idx === 0) return;
    const [entry] = placed.splice(idx, 1);
    placed.unshift(entry);
    const el = canvas.querySelector(`[data-swatch-id="${id}"]`);
    if (el) {
      // Keep the hint (if any) at the top of stacking - insert before the
      // first swatch sibling rather than as the literal first child.
      const firstSwatch = canvas.querySelector('.fk-swatch');
      if (firstSwatch) canvas.insertBefore(el, firstSwatch);
      else canvas.appendChild(el);
    }
    savePlaced();
    pushHistory();
  }

  // ── Variant swap popover ────────────────────────────────────────────────

  function closeSwapPopover() {
    const existing = document.querySelector('.fk-swap-popover');
    if (existing) existing.remove();
    document.removeEventListener('pointerdown', onSwapOutside, true);
  }

  function onSwapOutside(e) {
    if (e.target.closest('.fk-swap-popover, .fk-swatch__btn--swap')) return;
    closeSwapPopover();
  }

  function openSwapPopover(entry, swatchEl) {
    if (entry.type !== 'yarn') return;
    const yarn = findYarn(entry.vid);
    if (!yarn) return;
    const siblings = siblingsFor(yarn);
    if (siblings.length <= 1) return;

    closeSwapPopover();

    const pop = document.createElement('div');
    pop.className = 'fk-swap-popover';
    const head = document.createElement('p');
    head.className = 'fk-swap-popover__head t-mono';
    head.textContent = yarn.productTitle + ' · ' + bfT('fk_n_colors', '{count} Farben').replace('{count}', siblings.length);
    pop.appendChild(head);

    const grid = document.createElement('div');
    grid.className = 'fk-swap-popover__grid';

    siblings.forEach(s => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fk-swap-popover__chip';
      if (s.vid === entry.vid) btn.classList.add('is-current');
      btn.title = s.variantTitle && s.variantTitle !== 'Default Title'
        ? s.variantTitle : s.productTitle;
      btn.setAttribute('aria-label', btn.title);
      const img = document.createElement('img');
      img.src = s.image;
      img.alt = '';
      img.draggable = false;
      btn.appendChild(img);
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        swapVariant(entry.id, s.vid);
        closeSwapPopover();
      });
      grid.appendChild(btn);
    });
    pop.appendChild(grid);
    swatchEl.appendChild(pop);

    // Defer outside-click listener so the click that opened the popover
    // doesn't immediately close it.
    setTimeout(() => {
      document.addEventListener('pointerdown', onSwapOutside, true);
    }, 0);
  }

  function swapVariant(id, newVid) {
    const entry = placed.find(p => p.id === id);
    if (!entry || entry.type !== 'yarn') return;
    if (entry.vid === newVid) return;
    entry.vid = newVid;
    const old = canvas.querySelector(`[data-swatch-id="${id}"]`);
    if (old) old.remove();
    renderSwatch(entry);
    selectSwatch(id);
    savePlaced();
    pushHistory();
    updateCount();
    track('swap', findYarn(newVid));
  }

  function selectSwatch(id) {
    selectedId = id;
    canvas.querySelectorAll('.fk-swatch').forEach(el => {
      el.classList.toggle('fk-swatch--selected', el.dataset.swatchId === id);
    });
  }

  function renderAllPlaced() {
    canvas.querySelectorAll('.fk-swatch').forEach(el => el.remove());
    placed.forEach(renderSwatch);
    updateCount();
  }

  function renderSwatch(entry) {
    let imageSrc, displayName, priceStr, productHandle, vendor;

    if (entry.type === 'image') {
      imageSrc = entry.src;
      displayName = 'Eigenes Bild';
    } else {
      const yarn = findYarn(entry.vid);
      if (!yarn) return; // variant disappeared from catalog
      imageSrc = yarn.image;
      vendor = yarn.vendor;
      displayName = yarn.variantTitle && yarn.variantTitle !== 'Default Title'
        ? `${yarn.productTitle} · ${yarn.variantTitle}`
        : yarn.productTitle;
      priceStr = formatPrice(yarn.price);
      productHandle = yarn.handle;
    }

    const el = document.createElement('div');
    el.className = 'fk-swatch' + (entry.type === 'image' ? ' fk-swatch--image' : '');
    el.dataset.swatchId = entry.id;
    el.style.left = (entry.x * 100) + '%';
    el.style.top  = (entry.y * 100) + '%';
    el.style.setProperty('--fk-scale', entry.scale || 1);
    if (entry.type === 'image' && entry.w && entry.h) {
      el.style.setProperty('--fk-base-w', entry.w + 'px');
      el.style.setProperty('--fk-base-h', entry.h + 'px');
    }

    // .fk-swatch__media is the clip frame for the photo; label + tools
    // are siblings so they don't get clipped by overflow:hidden.
    const media = document.createElement('div');
    media.className = 'fk-swatch__media';

    const img = document.createElement('img');
    img.src = imageSrc;
    img.alt = displayName;
    img.className = 'fk-swatch__img';
    img.draggable = false;
    media.appendChild(img);
    el.appendChild(media);

    // Info label (visible on hover or selection - info only)
    const label = document.createElement('div');
    label.className = 'fk-swatch__label';

    if (vendor) {
      const v = document.createElement('span');
      v.className = 'fk-swatch__label-vendor';
      v.textContent = vendor;
      label.appendChild(v);
    }

    const name = document.createElement('span');
    name.className = 'fk-swatch__label-name';
    name.textContent = displayName;
    label.appendChild(name);

    if (priceStr) {
      const price = document.createElement('span');
      price.className = 'fk-swatch__label-price';
      price.textContent = priceStr;
      label.appendChild(price);
    }

    if (productHandle) {
      const link = document.createElement('a');
      link.href = `/products/${productHandle}`;
      link.className = 'fk-swatch__label-link';
      link.textContent = 'Zum Produkt →';
      link.addEventListener('pointerdown', (e) => e.stopPropagation());
      label.appendChild(link);
    }

    el.appendChild(label);

    // Action toolbar (visible only on selection - interactive). Order:
    // back, forward, [swap], smaller, bigger, remove. Swap only renders
    // when the yarn has sibling variants in the catalog.
    const tools = document.createElement('div');
    tools.className = 'fk-swatch__tools';

    const mkBtn = (cls, label, content, onClick) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'fk-swatch__btn' + (cls ? ' ' + cls : '');
      b.setAttribute('aria-label', label);
      b.title = label;
      if (typeof content === 'string') b.innerHTML = content;
      else b.appendChild(content);
      b.addEventListener('pointerdown', (e) => e.stopPropagation());
      b.addEventListener('click', (e) => { e.stopPropagation(); onClick(); });
      return b;
    };

    const sendBack    = mkBtn('', bfT('fk_send_back', 'Nach hinten'),  '⇣', () => sendToBack(entry.id));
    const bringFront  = mkBtn('', bfT('fk_bring_front', 'Nach vorne'),   '⇡', () => bringToFront(entry.id));
    const smaller     = mkBtn('', bfT('fk_smaller', 'Kleiner'),      '−', () => rescalePlaced(entry.id, -1));
    const bigger      = mkBtn('', bfT('fk_bigger', 'Größer'),       '+', () => rescalePlaced(entry.id, +1));
    const remove      = mkBtn('fk-swatch__btn--remove', bfT('remove', 'Entfernen'), '&times;', () => removePlaced(entry.id));

    tools.append(sendBack, bringFront);

    // Swap is only meaningful for yarn entries with sibling colorways.
    if (entry.type === 'yarn') {
      const yarnNow = findYarn(entry.vid);
      if (yarnNow && siblingsFor(yarnNow).length > 1) {
        const swap = mkBtn('fk-swatch__btn--swap', bfT('fk_other_color', 'Andere Farbe'), '⇆', () => {
          openSwapPopover(entry, el);
        });
        tools.appendChild(swap);
      }
    }

    tools.append(smaller, bigger, remove);
    el.appendChild(tools);

    if (selectedId === entry.id) {
      el.classList.add('fk-swatch--selected');
    }

    attachSwatchDrag(el, entry);
    canvas.appendChild(el);
  }

  function attachSwatchDrag(el, entry) {
    let dragState = null;
    el.addEventListener('pointerdown', (e) => {
      // Don't grab pointer capture if the user is interacting with one of
      // the swatch's own controls (size/swap/delete/zorder buttons), the
      // product link, or the swap popover that lives inside the swatch.
      // Otherwise the swatch's drag would steal events from those nested
      // elements and they'd never receive their click.
      if (e.target.closest('.fk-swatch__btn, .fk-swatch__label-link, .fk-swap-popover')) return;
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      el.classList.add('fk-swatch--dragging');
      const rect = canvas.getBoundingClientRect();
      dragState = {
        pointerId: e.pointerId,
        rect,
        startX: e.clientX,
        startY: e.clientY,
        moved: 0,
        dx: (e.clientX - rect.left) / rect.width  - entry.x,
        dy: (e.clientY - rect.top)  / rect.height - entry.y,
      };
    });

    el.addEventListener('pointermove', (e) => {
      if (!dragState || dragState.pointerId !== e.pointerId) return;
      dragState.moved = Math.max(
        dragState.moved,
        Math.hypot(e.clientX - dragState.startX, e.clientY - dragState.startY)
      );
      const { rect, dx, dy } = dragState;
      const x = clamp01((e.clientX - rect.left) / rect.width  - dx);
      const y = clamp01((e.clientY - rect.top)  / rect.height - dy);
      entry.x = x;
      entry.y = y;
      el.style.left = (x * 100) + '%';
      el.style.top  = (y * 100) + '%';
    });

    const finish = (e) => {
      if (!dragState || dragState.pointerId !== e.pointerId) return;
      el.classList.remove('fk-swatch--dragging');
      try { el.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
      const moved = dragState.moved;
      // Tap (no significant movement) → toggle selection. Drag → push
      // history so the move is undoable as a single step.
      if (moved < 5) {
        selectSwatch(selectedId === entry.id ? null : entry.id);
      }
      dragState = null;
      savePlaced();
      if (moved >= 5) pushHistory();
    };
    el.addEventListener('pointerup', finish);
    el.addEventListener('pointercancel', finish);
  }

  // ── Toolbar actions ──────────────────────────────────────────────────────

  // Undo / redo toolbar buttons.
  const undoBtn = root.querySelector('[data-fk-undo]');
  const redoBtn = root.querySelector('[data-fk-redo]');
  if (undoBtn) undoBtn.addEventListener('click', undo);
  if (redoBtn) redoBtn.addEventListener('click', redo);

  // Example loader on the empty hint.
  const exampleBtn = root.querySelector('[data-fk-example]');
  if (exampleBtn) {
    exampleBtn.addEventListener('click', loadExample);
  }

  function loadExample() {
    if (!palette.length) return;
    // Hexagonal cluster of up to 6 yarns - vary the scale slightly so
    // the result reads as composed, not stamped.
    const positions = [
      { x: 0.32, y: 0.38, scale: 1.00 },
      { x: 0.50, y: 0.30, scale: 1.25 },
      { x: 0.68, y: 0.40, scale: 1.00 },
      { x: 0.34, y: 0.62, scale: 0.85 },
      { x: 0.55, y: 0.65, scale: 1.10 },
      { x: 0.72, y: 0.60, scale: 1.00 },
    ];
    const shuffled = [...palette].sort(() => Math.random() - 0.5);
    const yarns = shuffled.slice(0, Math.min(positions.length, shuffled.length));
    placed = yarns.map((y, i) => ({
      id: uid(),
      type: 'yarn',
      vid: y.vid,
      x: positions[i].x,
      y: positions[i].y,
      scale: positions[i].scale,
    }));
    selectedId = null;
    closeSwapPopover();
    renderAllPlaced();
    savePlaced();
    pushHistory();
    updateCount();
    track('example');
  }

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      if (!placed.length) return;
      // No confirm() - Chrome blocks modal dialogs inside cross-origin
      // iframes (e.g. the Shopify theme editor preview), so it would
      // silently return false. Undo handles accidental clears.
      placed = [];
      canvas.querySelectorAll('.fk-swatch').forEach(el => el.remove());
      selectedId = null;
      closeSwapPopover();
      savePlaced();
      pushHistory();
      updateCount();
      track('clear');
      showNotice('Tisch geleert. Mit ↶ rückgängig machen.');
    });
  }

  // ── Inline notice (non-blocking) ────────────────────────────────────────

  let noticeTimer = 0;
  function showNotice(message, options) {
    const el = root.querySelector('[data-fk-notice]');
    if (!el) { console.warn('[farbkombi]', message); return; }
    const isError = options && options.error;
    el.textContent = message;
    el.classList.toggle('fk-notice--error', !!isError);
    el.hidden = false;
    // Force reflow so the transition runs from the hidden start state.
    void el.offsetHeight;
    el.classList.add('is-visible');
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.hidden = true; }, 220);
    }, (options && options.duration) || 3200);
  }

  // ── Export canvas as PNG ────────────────────────────────────────────────

  const exportBtn = root.querySelector('[data-fk-export]');
  if (exportBtn) {
    exportBtn.addEventListener('click', async () => {
      if (!placed.length) return;
      const span = exportBtn.querySelector('span');
      const original = span ? span.textContent : '';
      if (span) span.textContent = bfT('fk_generating', 'Bild wird erzeugt …');
      exportBtn.disabled = true;
      try {
        const dataUrl = await renderCanvasPng();
        const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `farbkombi-${date}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        markCompletion('export');
      } catch (err) {
        console.warn('[farbkombi] export failed:', err);
        showNotice(bfT('fk_image_gen_error', 'Bild konnte nicht erzeugt werden - bitte erneut versuchen.'), { error: true });
      } finally {
        if (span) span.textContent = original;
        exportBtn.disabled = false;
      }
    });
  }

  // Renders all placed swatches onto an offscreen <canvas> with the same
  // proportions as the live one. Each swatch is clipped (circle for yarns,
  // rounded square for uploaded images) and the photo is drawn cover-fit.
  // Returns a PNG data URL. No external library - `crossOrigin: anonymous`
  // is enough because Shopify CDN images come with permissive CORS headers.
  async function renderCanvasPng() {
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = rect.width;
    const h = rect.height;

    const off = document.createElement('canvas');
    off.width = Math.round(w * dpr);
    off.height = Math.round(h * dpr);
    const ctx = off.getContext('2d');
    ctx.scale(dpr, dpr);

    // Paper-coloured background - read from CSS custom property so it
    // matches the live theme even if the palette shifts.
    const paper = (getComputedStyle(document.documentElement)
      .getPropertyValue('--paper').trim()) || '#F3EFE8';
    ctx.fillStyle = paper;
    ctx.fillRect(0, 0, w, h);

    // Soft radial vignette to echo the live canvas without being noisy.
    const vignette = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.hypot(w, h) / 2);
    vignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
    vignette.addColorStop(1, 'rgba(0, 0, 0, 0.04)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);

    // Resolve image source per entry - uploaded images are dataURLs (no
    // CORS concern); yarn entries pull from the cached palette.
    const items = placed.map(entry => {
      let src;
      if (entry.type === 'image') src = entry.src;
      else {
        const yarn = findYarn(entry.vid);
        if (!yarn) return null;
        src = yarn.image;
      }
      return { entry, src };
    }).filter(Boolean);

    const loaded = await Promise.all(items.map(({ entry, src }) =>
      loadImageForExport(src).then(img => ({ entry, img })).catch(() => null)
    ));

    loaded.filter(Boolean).forEach(({ entry, img }) => {
      const scale = entry.scale || 1;
      const baseW = entry.type === 'image' ? (entry.w || 128) : 96;
      const baseH = entry.type === 'image' ? (entry.h || 128) : 96;
      const sw = baseW * scale;
      const sh = baseH * scale;
      const cx = entry.x * w;
      const cy = entry.y * h;
      const halfW = sw / 2;
      const halfH = sh / 2;

      const traceShape = () => {
        ctx.beginPath();
        if (entry.type === 'image') {
          const r = 6;
          const x = cx - halfW, y = cy - halfH;
          ctx.moveTo(x + r, y);
          ctx.lineTo(x + sw - r, y);
          ctx.quadraticCurveTo(x + sw, y, x + sw, y + r);
          ctx.lineTo(x + sw, y + sh - r);
          ctx.quadraticCurveTo(x + sw, y + sh, x + sw - r, y + sh);
          ctx.lineTo(x + r, y + sh);
          ctx.quadraticCurveTo(x, y + sh, x, y + sh - r);
          ctx.lineTo(x, y + r);
          ctx.quadraticCurveTo(x, y, x + r, y);
        } else {
          // Yarn = circle (square base)
          ctx.arc(cx, cy, sw / 2, 0, Math.PI * 2);
        }
      };

      // Soft shadow under the swatch.
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.18)';
      ctx.shadowBlur = 16;
      ctx.shadowOffsetY = 6;
      traceShape();
      ctx.fillStyle = paper;
      ctx.fill();
      ctx.restore();

      // Clip and draw image cover-fit inside the same shape.
      ctx.save();
      traceShape();
      ctx.clip();
      const ratio = Math.max(sw / img.width, sh / img.height);
      const dw = img.width * ratio;
      const dh = img.height * ratio;
      ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
      ctx.restore();
    });

    return off.toDataURL('image/png');
  }

  function loadImageForExport(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Shopify CDN serves images with Access-Control-Allow-Origin: *.
      // The crossOrigin attribute makes the browser request CORS headers,
      // and once it has them the canvas stays untainted on toDataURL.
      if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image load: ' + src));
      img.src = src;
    });
  }

  if (shareBtn) {
    shareBtn.addEventListener('click', async () => {
      markCompletion('share');
      const encoded = encodePlaced(placed);
      const url = encoded
        ? `${location.origin}${location.pathname}?canvas=${encoded}`
        : `${location.origin}${location.pathname}`;
      const span = shareBtn.querySelector('span');
      const original = span ? span.textContent : '';
      const copied = await copyText(url);
      if (copied) {
        if (span) span.textContent = 'Link kopiert ✓';
        setTimeout(() => { if (span) span.textContent = original; }, 2000);
      } else {
        // Both clipboard paths failed (typical inside cross-origin
        // iframes). Render the URL inline so the user can ⌘C it.
        showShareUrl(url);
      }
    });
  }

  function showShareUrl(url) {
    let panel = root.querySelector('[data-fk-share-panel]');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'fk-share-panel';
      panel.dataset.fkSharePanel = '';
      const hint = document.createElement('p');
      hint.className = 'fk-share-panel__hint t-mono';
      hint.textContent = bfT('fk_copy_blocked', 'Kopieren ist im Vorschau-Fenster blockiert - bitte den Link mit ⌘C / Ctrl+C kopieren:');
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'fk-share-panel__input';
      input.readOnly = true;
      input.dataset.fkShareInput = '';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'fk-share-panel__close';
      close.setAttribute('aria-label', 'Schließen');
      close.innerHTML = '&times;';
      close.addEventListener('click', () => panel.remove());
      panel.append(hint, input, close);
      const toolbar = root.querySelector('.fk-toolbar');
      if (toolbar && toolbar.parentNode) {
        toolbar.parentNode.insertBefore(panel, toolbar.nextSibling);
      } else {
        root.appendChild(panel);
      }
    }
    const input = panel.querySelector('[data-fk-share-input]');
    input.value = url;
    input.focus();
    input.select();
  }

  async function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try { await navigator.clipboard.writeText(text); return true; }
      catch (_) { /* permission policy or insecure context - fall through */ }
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.left = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch (_) { return false; }
  }

  if (cartBtn) {
    cartBtn.addEventListener('click', async () => {
      if (!placed.length) return;
      cartBtn.dataset.state = 'loading';
      cartLabel.textContent = bfT('adding', 'Wird hinzugefügt …');

      // Add each variant. Group by vid so duplicates collapse to a quantity bump.
      const counts = new Map();
      placed.forEach(p => counts.set(p.vid, (counts.get(p.vid) || 0) + 1));

      const items = [];
      counts.forEach((qty, vid) => {
        // vid is already the legacy numeric id from loadPalette().
        if (!vid) return;
        items.push({ id: vid, quantity: qty });
      });

      if (!items.length) {
        cartBtn.dataset.state = '';
        cartLabel.textContent = bfT('add_to_cart', 'In den Warenkorb');
        return;
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        // Try to parse the body either way - Shopify returns the failure
        // reason as JSON with status/message/description fields.
        let body = null;
        try { body = await res.json(); } catch (_) { /* not JSON */ }

        if (!res.ok) {
          const detail = body
            ? `${body.message || 'Cart Error'}${body.description ? ' - ' + body.description : ''}`
            : `HTTP ${res.status}`;
          console.warn('[farbkombi] cart add failed:', detail, { items, body });
          showNotice(bfT('add_failed', 'Hinzufügen fehlgeschlagen: ') + detail, { error: true, duration: 5000 });
          throw new Error(detail);
        }

        const cart = await fetch('/cart.js').then(r => r.json());
        document.dispatchEvent(new CustomEvent('boni:cart:added', { detail: cart }));
        markCompletion('cart');
        cartBtn.dataset.state = 'success';
        cartLabel.textContent = bfT('added', '✓ Hinzugefügt');
        setTimeout(() => {
          cartBtn.dataset.state = '';
          cartLabel.textContent = bfT('add_to_cart', 'In den Warenkorb');
        }, 2000);
      } catch (err) {
        console.warn('[farbkombi] cart add error:', err);
        cartBtn.dataset.state = '';
        cartLabel.textContent = bfT('error_retry', 'Fehler - erneut versuchen');
        setTimeout(() => { cartLabel.textContent = bfT('add_to_cart', 'In den Warenkorb'); }, 2500);
      }
    });
  }

  if (searchInput) {
    let timer = 0;
    searchInput.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => renderPalette(searchInput.value), 120);
    });
  }

  // ── File upload (user images on canvas) ─────────────────────────────────

  const uploadBtn = root.querySelector('[data-fk-upload]');
  const uploadInput = root.querySelector('[data-fk-upload-input]');

  if (uploadBtn && uploadInput) {
    uploadBtn.addEventListener('click', () => uploadInput.click());
    uploadInput.addEventListener('change', async () => {
      const file = uploadInput.files && uploadInput.files[0];
      uploadInput.value = '';
      if (!file) return;
      try {
        const result = await resizeImageFile(file, 400);
        const display = fitToBox(result.naturalW, result.naturalH, 128);
        const jitter = () => 0.4 + Math.random() * 0.2;
        addPlacedImage(result.src, jitter(), jitter(), display.w, display.h);
        track('upload');
      } catch (err) {
        console.warn('[farbkombi] upload failed', err);
        showNotice(bfT('fk_image_error', 'Bild konnte nicht geladen werden.'), { error: true });
      }
    });
  }

  function fitToBox(natW, natH, maxDim) {
    if (!natW || !natH) return { w: maxDim, h: maxDim };
    const ratio = Math.min(maxDim / natW, maxDim / natH, 1);
    return {
      w: Math.max(40, Math.round(natW * ratio)),
      h: Math.max(40, Math.round(natH * ratio)),
    };
  }

  // Resize the uploaded image client-side to keep localStorage payloads
  // reasonable. JPEG re-encode at 0.85 quality usually lands ~30–80 KB.
  // Returns both the dataURL and the natural dimensions so the caller
  // can preserve aspect on the canvas.
  function resizeImageFile(file, maxDim) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('decode'));
        img.onload = () => {
          const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
          const w = Math.round(img.width * ratio);
          const h = Math.round(img.height * ratio);
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve({
            src: c.toDataURL('image/jpeg', 0.85),
            naturalW: img.width,
            naturalH: img.height,
          });
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────

  (async function init() {
    const result = await loadPalette();
    if (loadingMsg) loadingMsg.remove();

    if (result.ok) {
      palette = result.data;
      buildSiblingsMap();
      if (palette.length) {
        renderPalette('');
      } else if (paletteEmpty) {
        paletteEmpty.hidden = false;
        paletteEmpty.textContent = bfT('fk_collection_empty', 'Die Kollektion ist leer oder enthält keine Produkte mit Bild.');
      }
    } else {
      if (paletteEmpty) {
        paletteEmpty.hidden = false;
        paletteEmpty.textContent = result.error;
      }
      console.warn('[farbkombi] palette load failed:', result.error);
    }

    // Hydrate placed regardless of palette state - uploaded images don't
    // need the palette, and yarn entries just silently skip if their vid
    // isn't in the loaded catalog.
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('canvas');
    if (fromUrl) {
      placed = decodePlaced(fromUrl);
      savePlaced();
    } else {
      placed = loadPlaced();
    }
    renderAllPlaced();

    // Seed history so the first undoable action has something to revert to.
    history = [snapshot()];
    historyIndex = 0;
    updateHistoryButtons();
  })();

  // ── Keyboard: undo / redo ───────────────────────────────────────────────

  window.addEventListener('keydown', (e) => {
    // Don't intercept while the user is typing in the search input or any
    // editable field elsewhere on the page.
    const tag = e.target && e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return;
    const meta = e.metaKey || e.ctrlKey;
    if (!meta) return;
    if (e.key === 'z' || e.key === 'Z') {
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
    } else if (e.key === 'y' || e.key === 'Y') {
      e.preventDefault();
      redo();
    }
  });
})();
